import axios, {
  AxiosRequestConfig,
  AxiosResponse,
  AxiosError,
  InternalAxiosRequestConfig,
} from 'axios';

const AUTH_FAILURE_PATTERN = /invalid|expired|token|unauthorized/i;
let authRedirectPending = false;

const clearSessionAndRedirectToLogin = () => {
  if (authRedirectPending) return;
  const path = window.location.pathname;
  if (path === '/' || path === '/login') return;

  authRedirectPending = true;
  localStorage.removeItem('authData');
  window.location.replace('/');
};

// Create an axios instance with default configuration
const api = axios.create({
  baseURL: process.env.REACT_APP_SERVER_API_BASE_URL || 'http://localhost:8900',
  headers: {
    'Content-Type': 'application/json',
    'ngrok-skip-browser-warning': 'true', // to skip browser warning while using ngrok
    // Optionally include Authorization header if needed
  },
});

console.log('PROCCESS', process.env.REACT_APP_SERVER_API_BASE_URL);
// Request Interceptor to add Authorization header dynamically from context store
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const storedData = localStorage.getItem('authData');
    if (storedData) {
      const parsed = JSON.parse(storedData);
      if (parsed.token) {
        config.headers['Authorization'] = `Bearer ${parsed.token}`;
      }
    }
    return config;
  },
  (error: AxiosError) => Promise.reject(error),
);

api.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    const status = error.response?.status;
    const message =
      (error.response?.data as { message?: string } | undefined)?.message || '';

    const isAuthFailure =
      status === 401 ||
      (status === 403 && AUTH_FAILURE_PATTERN.test(message));

    if (isAuthFailure) {
      clearSessionAndRedirectToLogin();
    }

    return Promise.reject(error);
  },
);

// Function to handle GET requests
export const get = async (
  url: string,
  config?: AxiosRequestConfig,
): Promise<AxiosResponse> => {
  try {
    const response: AxiosResponse = await api.get(url, config);
    return response;
  } catch (error) {
    handleError(error);
    throw new Error(getErrorMessage(error)); // Rethrow with a message
  }
};

// Function to handle POST requests
export const post = async (
  url: string,
  data?: any,
  config?: AxiosRequestConfig,
): Promise<AxiosResponse> => {
  try {
    // Perform the POST request
    const response: AxiosResponse = await api.post(url, data, config);

    // Return the full Axios response
    return response;
  } catch (error) {
    // Handle error
    handleError(error);

    // Rethrow the error with a meaningful message
    throw new Error(getErrorMessage(error));
  }
};

// Function to handle PUT requests
export const put = async (
  url: string,
  data: any,
  config?: AxiosRequestConfig,
): Promise<any> => {
  try {
    const response: AxiosResponse = await api.put(url, data, config);
    return response.data;
  } catch (error) {
    handleError(error);
    throw new Error(getErrorMessage(error)); // Rethrow with a message
  }
};

// Function to handle PATCH requests
export const patch = async (
  url: string,
  data: any,
  config?: AxiosRequestConfig,
): Promise<any> => {
  try {
    const response: AxiosResponse = await api.patch(url, data, config);
    return response.data;
  } catch (error) {
    handleError(error);
    throw new Error(getErrorMessage(error)); // Rethrow with a message
  }
};

// Function to handle DELETE requests
export const del = async (
  url: string,
  config?: AxiosRequestConfig,
): Promise<any> => {
  try {
    const response: AxiosResponse = await api.delete(url, config);
    return response.data;
  } catch (error) {
    handleError(error);
    throw new Error(getErrorMessage(error)); // Rethrow with a message
  }
};

// Helper function to get a proper error message from the AxiosError
const getErrorMessage = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    // Check if error.response exists for more detailed error handling
    if (error.response) {
      return (
        error.response.data?.message ||
        `Error: ${error.response.status} - ${error.response.statusText}`
      );
    } else if (error.request) {
      // Handle when no response was received from the server
      return 'No response received from the server. Please check your network connection or try again later.';
    } else {
      // Handle Axios request configuration errors
      return `Error in request setup: ${error.message}`;
    }
  }
  // Fallback for non-Axios errors
  return 'An unknown error occurred. Please try again later.';
};

// Centralized error logging
const handleError = (error: unknown): void => {
  if (axios.isAxiosError(error)) {
    if (error.response) {
      // Handle server-side errors (e.g., 4xx, 5xx)
      console.error(
        `API Error: ${error.response.status} ${error.response.statusText}`,
      );
      console.error('Response data:', error.response.data);
    } else if (error.request) {
      // Handle no response scenario
      console.error('No response received:', error.request);
      console.error('Possible network issue or CORS problem.');
    } else {
      // Handle configuration or setup errors
      console.error('Request setup error:', error.message);
    }
  } else {
    // Handle non-Axios errors (e.g., thrown from other sources)
    console.error('Non-Axios error:', error);
  }
};
